import { useState, useEffect } from 'react';
import { editorialBannerAPI, IMAGE_BASE } from '../../services/api';
import { Save, Upload, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';

export default function EditorialBannerAdmin() {
    const [banner, setBanner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [newImage, setNewImage] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        editorialBannerAPI.get()
            .then(res => setBanner(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (field, value) => {
        setBanner(prev => ({ ...prev, [field]: value }));
    };

    const handleImageChange = (file) => {
        if (!file) return;
        setNewImage(file);
        setPreview(URL.createObjectURL(file));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('overtitle', banner.overtitle || '');
            formData.append('title', banner.title || '');
            formData.append('description', banner.description || '');
            formData.append('buttonText', banner.buttonText || '');
            formData.append('buttonLink', banner.buttonLink || '');
            formData.append('isActive', banner.isActive);
            if (newImage) formData.append('image', newImage);
            const res = await editorialBannerAPI.update(formData);
            setBanner(res.data);
            setNewImage(null);
            setPreview(null);
            setMessage('Kaydedildi.');
        } catch (e) {
            console.error(e);
            setMessage('Hata: Kaydedilemedi.');
        }
        setSaving(false);
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;
    if (!banner) return <div className="admin-page"><p>Banner bulunamadı.</p></div>;

    const imgSrc = preview || (banner.image ? (banner.image.startsWith('http') ? banner.image : IMAGE_BASE + banner.image) : null);

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h1>Editorial Banner (Yeni Sezon)</h1>
                <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '4px' }}>
                    Anasayfadaki büyük görsel + yazı banner'ını buradan yönetebilirsiniz.
                </p>
            </div>

            {message && (
                <div className={`admin-alert ${message.startsWith('Hata') ? 'admin-alert-error' : 'admin-alert-success'}`}>
                    {message}
                </div>
            )}

            <div className="editorial-admin-layout">
                {/* Preview */}
                <div className="editorial-admin-preview">
                    <h3 style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#6b7280' }}>Önizleme</h3>
                    <div className="editorial-preview-card">
                        <div className="editorial-preview-img">
                            {imgSrc ? (
                                <img src={imgSrc} alt="Banner" />
                            ) : (
                                <div className="editorial-preview-placeholder">
                                    <ImageIcon size={48} />
                                    <span>Görsel Yok</span>
                                </div>
                            )}
                        </div>
                        <div className="editorial-preview-content">
                            <span className="editorial-preview-overtitle">{banner.overtitle}</span>
                            <h2 dangerouslySetInnerHTML={{ __html: banner.title }} />
                            <p>{banner.description}</p>
                            <span className="editorial-preview-btn">{banner.buttonText}</span>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="editorial-admin-form">
                    <div className="form-group">
                        <label>Görsel</label>
                        <div className="editorial-image-upload">
                            {imgSrc && <img src={imgSrc} alt="Banner" className="editorial-thumb" />}
                            <label className="homepage-cat-upload-btn" style={{ position: 'relative' }}>
                                <Upload size={14} /> Görsel Seç
                                <input type="file" accept="image/*" hidden onChange={(e) => handleImageChange(e.target.files[0])} />
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Üst Başlık (Overtitle)</label>
                        <input type="text" value={banner.overtitle || ''} onChange={(e) => handleChange('overtitle', e.target.value)} placeholder="Yeni Sezon" />
                    </div>

                    <div className="form-group">
                        <label>Başlık (HTML destekli, satır sonu için &lt;br /&gt;)</label>
                        <input type="text" value={banner.title || ''} onChange={(e) => handleChange('title', e.target.value)} placeholder="İlkbahar / Yaz<br />Koleksiyonu" />
                    </div>

                    <div className="form-group">
                        <label>Açıklama</label>
                        <textarea rows={3} value={banner.description || ''} onChange={(e) => handleChange('description', e.target.value)} placeholder="Açıklama metni..." />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                            <label>Buton Metni</label>
                            <input type="text" value={banner.buttonText || ''} onChange={(e) => handleChange('buttonText', e.target.value)} placeholder="Koleksiyonu Keşfet" />
                        </div>
                        <div className="form-group">
                            <label>Buton Linki</label>
                            <input type="text" value={banner.buttonLink || ''} onChange={(e) => handleChange('buttonLink', e.target.value)} placeholder="/urunler" />
                        </div>
                    </div>

                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={banner.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />
                            {banner.isActive ? <><Eye size={14} /> Aktif (anasayfada görünür)</> : <><EyeOff size={14} /> Pasif (anasayfada gizli)</>}
                        </label>
                    </div>

                    <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center', padding: '12px' }}>
                        <Save size={16} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
}
